import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getLocale } from '../../helpers/dates';
import { SkeletonReservationDetail } from '../Skeletons/SkeletonReservationDetail';
import { useTranslation } from 'react-i18next';
import Badge from '../Badge/Badge';
import CopyIcon from '../icons/copyIcon';
import { Toast } from '../Common/Toast/Toast';
import './reservation-details.css';
import Money from '../Common/Money/Money';

const ReservationDetailLink = ({ reservation }) => {
  const { t } = useTranslation();
  const app = useSelector((state) => state.app);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    const link = reservation?.detail?.paymentInformation?.paymentLink;
    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const locale = getLocale(app.language);

  const getStatus = (reservationStatus) => {
    switch (reservationStatus) {
      case 'PENDING':
        return <Badge text={t('reservationStatus.pending')} type={'pending'} />;
      case 'WAITING_DEPOSIT':
        return <Badge text={t('reservationStatus.pendingPayment')} type={'pending'} />;
      case 'CONFIRMED':
      case 'CHECKIN':
        return <Badge text={t('reservationStatus.confirmed')} type={'active'} />;
      case 'CANCELLED':
        return <Badge text={t('reservationStatus.cancelled')} type={'cancelled'} />;
      case 'EXPIRED':
        return <Badge text={t('reservationStatus.expired')} type={'expired'} />;
      case 'REJECTED':
        return <Badge text={t('reservationStatus.rejected')} type={'cancelled'} />;
      case 'NO_SHOW':
      case 'NO_SHOW_MANUAL':
        return <Badge text={t('reservationStatus.noshow')} type={'expired'} />;
      default:
        return <Badge text={t('reservationStatus.finished')} type={'active'} />;
    }
  };

  const reservationPaymentInfo = reservation?.detail?.reservationProducts?.paymentDetails;
  return (
    <>
      {loading || !reservation ? (
        SkeletonReservationDetail()
      ) : (
        <>
          <div className='order-details fade-up'>
            <div className='card px-4'>
              <div className='mt-3'>
                <div className='row'>
                  {reservation.eventShow ? (
                    <div className='col show-name'>
                      <div>{reservation.eventShow.name}</div>
                      <div className='font-small text-secondary'>{reservation.showName}</div>
                    </div>
                  ) : (
                    <div className='col show-name'>{reservation.showName}</div>
                  )}

                  <div className='col-auto my-auto pl-0'>{getStatus(reservation.status)}</div>
                </div>
              </div>

              <div className=' mb-3 '>
                {reservation?.detail?.paymentInformation?.paymentLink && (
                  <>
                    <div className='text-secondary text-medium fw-semibold mt-3'>
                      {t('paymentLink')}
                    </div>
                    <div className='position-relative text-truncate pr-5 mt-1'>
                      <a target='_noblank' href={reservation.detail.paymentInformation.paymentLink}>
                        {reservation.detail.paymentInformation.paymentLink}
                      </a>{' '}
                      <span
                        onClick={handleCopyLink}
                        style={{ position: 'absolute', right: '1.5rem', top: '-2px' }}
                      >
                        <CopyIcon size={19} fillColor='#00A3FF' />
                      </span>
                      {copied && (
                        <Toast
                          bottom={'15px'}
                          top={false}
                          type={'success'}
                          message={t('linkCopied')}
                          delay={20}
                        />
                      )}
                    </div>{' '}
                  </>
                )}

                <div className='text-secondary text-medium fw-semibold mt-3'>{t('sent')}</div>
                <div className='fw-semibold mt-1'>{reservation?.customerName}</div>
                <div className='fw-semibold mt-1'>{reservation?.emailclient}</div>

                <div className='mt-1 fw-bold'>{reservation?.customerPhone}</div>

                {reservation?.detail?.paymentInformation?.expirePayment && (
                  <>
                    {' '}
                    <div className='text-secondary text-medium fw-semibold mt-3'>
                      {t('dateAndLimitTime')}
                    </div>
                    <div className='fw-bold sentences-capitalize mt-1'>
                      {format(
                        new Date(reservation.detail.paymentInformation.expirePayment),
                        locale.code === 'es'
                          ? "EEEE, d 'de' MMMM 'a las' h:mm aaa"
                          : "EEEE, d MMMM '-' h:mm aaa",
                        {
                          locale,
                        },
                      )}
                    </div>{' '}
                  </>
                )}

                <div className='text-secondary text-medium fw-semibold mt-3'>{t('status')}</div>
                <div className='fw-bold mt-1 mb-3'>{t('paymentStatus.' + reservation.status)}</div>

                {reservation?.description?.length > 0 && (
                  <div className='borderTop'>
                    <div className='fw-bold mt-3'>{t('description')}</div>
                    <div className='font-small text-lighter-gray'>
                      {reservation.description.map((item, i) => (
                        <div className='row mt-2' key={i}>
                          <div className='col pr-0'>{item}</div>
                        </div>
                      ))}
                      <div className='row mt-2 mb-3'>
                        <div className='col pr-0'>{t('internalReference')}:</div>
                        <div className='col-auto'>{reservation.externalReferenceCode}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className='mt-2 borderTop'>
                  {reservation?.payment && <div className='bold mt-3'>{t('summary')}</div>}
                  {reservation?.payment?.depositAmount > 0 && (
                    <div className='row mt-1'>
                      <div className='col pr-0'>{t('totalReservation')}:</div>
                      <div className='col-auto'>
                        <Money
                          value={reservation?.payment?.remainingDepositAmount}
                          symbolSize='14px'
                          lowDecimals
                          decimal={false}
                        />
                      </div>
                    </div>
                  )}
                  {reservation?.payment?.depositAmount > 0 && (
                    <div className='row mt-2'>
                      <div className='col bold font-small pr-0'>{t('total')}:</div>
                      <div className='col-auto bold'>
                        <Money
                          value={reservation?.payment?.depositAmount}
                          symbolSize='14px'
                          decimal={false}
                          lowDecimals
                        />
                      </div>
                    </div>
                  )}
                </div>

                {reservationPaymentInfo?.map((payment) => (
                  <>
                    <div className='bold pt-3 mt-4 mb-3 borderTop'>{t('paymentInformation')}</div>
                    <div className='row font-small ' key={payment.chargeId}>
                      <div className='col'>
                        <div className='fw-bold '>
                          {t('payment')} #{payment.chargeId}
                        </div>
                        <div className='text-secondary font-tiny mt-1'>
                          <div>
                            <span className='text-capitalize'>{payment.brand}</span>
                            (XXXX XXXX XXXX {payment.last4})
                          </div>
                          <div>
                            {format(new Date(payment.paymentLocalDate), 'PPP h:mm aaa', {
                              locale,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className='col-auto'>
                        <Money value={payment.amount} />
                      </div>
                    </div>
                  </>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReservationDetailLink;
